import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import {
  apiGetBookById,
  apiGetBooks,
  apiAddReview,
  apiMarkHelpful,
  apiGetMyHelpfulReviews,
} from '../api';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { sortReviews, calculateAvgRating, getRatingDistribution } from '../utils/reviewHelpers';
import { getBookImage } from '../utils';
import { isProductABook } from '../utils/constants';
import ImageZoom from '../components/shop/ImageZoom';
import StockOverlay from '../components/common/StockOverlay';
import RelatedProducts from '../components/product/RelatedProducts';
import AttributesSlider from '../components/product/AttributesSlider';
import BuyBox from '../components/product/BuyBox';
import ProductInfo from '../components/product/ProductInfo';
import ProductDetailsBlock from '../components/product/ProductDetailsBlock';
import ProductDescription from '../components/product/ProductDescription';
import ReviewsSection from '../components/product/ReviewsSection';
import { addRecentlyViewed } from '../utils/recentlyViewed';
import SEO from '../components/common/SEO';
import logger from '../utils/logger';
import { useCurrency } from '../context/CurrencyContext';
import { getPurchaseIssue } from '../utils/stock';

const mapReviews = (raw) =>
  (raw || []).map((r) => ({
    id: r.id,
    user: r.userName || r.user || 'Anonymous',
    rating: r.rating,
    text: r.text || r.comment || '',
    helpful: r.helpful || 0,
    date: r.createdAt || r.date || new Date().toISOString(),
  }));

const ProductDetail = ({ book: bookProp }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { isAuthenticated } = useAuth();
  const { addToast } = useToast();
  const { getBookAmount, getBookDiscount } = useCurrency();

  const [book, setBook] = useState(bookProp || null);
  const [related, setRelated] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [loading, setLoading] = useState(!bookProp);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, text: '' });
  const [reviewSort, setReviewSort] = useState('newest');
  const [helpfulReviewIds, setHelpfulReviewIds] = useState(() => new Set());
  const [helpfulPendingIds, setHelpfulPendingIds] = useState(() => new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      setHelpfulReviewIds(new Set());
      return;
    }
    apiGetMyHelpfulReviews()
      .then(({ reviewIds = [] }) => setHelpfulReviewIds(new Set(reviewIds)))
      .catch((error) => logger.error('Failed to load helpful review votes', error));
  }, [isAuthenticated]);

  useEffect(() => {
    if (bookProp) {
      setBook(bookProp);
      setReviews(mapReviews(bookProp.reviews));
      addRecentlyViewed(bookProp);
      if (bookProp.categoryId) {
        apiGetBooks(`category=${bookProp.categoryId}&limit=8`)
          .then((res) => {
            setRelated((res.books || []).filter((x) => x.id !== bookProp.id).slice(0, 6));
          })
          .catch((e) => logger.error('Failed to load related books', e));
      }
      return;
    }
    setLoading(true);
    apiGetBookById(id)
      .then((data) => {
        const b = data.book || data;
        setBook(b);
        setReviews(mapReviews(b.reviews));
        addRecentlyViewed(b);
        if (b.categoryId) {
          apiGetBooks(`category=${b.categoryId}&limit=8`)
            .then((res) => setRelated((res.books || []).filter((x) => x.id !== b.id).slice(0, 6)))
            .catch((e) => logger.error('Failed to load related books', e));
        }
      })
      .catch((e) => logger.error('Failed to load product', e))
      .finally(() => setLoading(false));
  }, [id, bookProp]);

  useEffect(() => {
    if (!book?.id) return;
    let cancelled = false;
    apiGetBooks('sort=newest&limit=12')
      .then((res) => {
        if (cancelled) return;
        setNewReleases((res.books || []).filter((item) => item.id !== book.id));
      })
      .catch((error) => logger.error('Failed to load new releases', error));
    return () => {
      cancelled = true;
    };
  }, [book?.id]);

  const displayReviews = reviews;
  const avgRating = calculateAvgRating(displayReviews, book?.rating || 0);
  const ratingDistribution = getRatingDistribution(displayReviews);
  const sortedReviews = sortReviews(displayReviews, reviewSort);
  const inWishlist = book ? isInWishlist(book.id) : false;
  const isOutOfStock = book && book.stock !== undefined && book.stock !== null && book.stock <= 0;
  const purchaseIssue = book ? getPurchaseIssue({ ...book, quantity: qty }, getBookAmount(book)) : '';
  const isBook = isProductABook(book);
  const marketDiscount = getBookDiscount(book);
  const categoryName = book?.category?.name || (isBook ? 'Books' : 'Products');

  const handleAddToCart = useCallback(() => {
    if (purchaseIssue) {
      addToast(purchaseIssue, 'info');
      return;
    }
    addToCart(book, qty);
    addToast(`Added ${qty} \u00d7 "${book.title}" to cart`, 'success');
  }, [book, qty, purchaseIssue, addToCart, addToast]);

  const handleBuyNow = useCallback(() => {
    if (purchaseIssue) return addToast(purchaseIssue, 'info');
    addToCart(book, qty);
    if (!isAuthenticated) {
      addToast('Please sign in to continue checkout', 'info');
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }
    navigate('/checkout');
  }, [book, qty, purchaseIssue, isAuthenticated, addToCart, addToast, navigate]);

  const handleWishlist = useCallback(() => {
    if (inWishlist) {
      removeFromWishlist(book.id);
      addToast('Removed from wishlist', 'info');
    } else {
      addToWishlist(book);
      addToast('Added to wishlist', 'success');
    }
  }, [book, inWishlist, addToWishlist, removeFromWishlist, addToast]);

  const handleSubmitReview = useCallback(async (e) => {
    e.preventDefault();
    if (!reviewData.text.trim()) return;
    try {
      const data = await apiAddReview(book.id, reviewData.rating, reviewData.text);
      setReviews((prev) => [data.review, ...prev]);
      setReviewData({ rating: 5, text: '' });
      setShowReviewForm(false);
      addToast('Review submitted successfully!', 'success');
    } catch (err) {
      addToast(err.message || 'Failed to submit review', 'error');
    }
  }, [book, reviewData, addToast]);

  const handleHelpful = useCallback(async (rid) => {
    if (!isAuthenticated) {
      addToast('Please sign in to mark a review as helpful', 'info');
      navigate('/login');
      return;
    }
    if (helpfulReviewIds.has(rid) || helpfulPendingIds.has(rid)) return;
    setHelpfulPendingIds((prev) => new Set(prev).add(rid));
    try {
      const data = await apiMarkHelpful(rid);
      setReviews((prev) => prev.map((r) => (r.id === rid ? { ...r, helpful: data.helpful } : r)));
      setHelpfulReviewIds((prev) => new Set(prev).add(rid));
    } catch (err) {
      logger.error('Failed to mark helpful', err);
      addToast(err.message || 'Failed to mark review as helpful', 'error');
    } finally {
      setHelpfulPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(rid);
        return next;
      });
    }
  }, [isAuthenticated, helpfulReviewIds, helpfulPendingIds, addToast, navigate]);

  if (loading) {
    return (
      <div className="w-full mx-auto px-4 py-16 text-center">
        <p className="text-black">Loading...</p>
      </div>
    );
  }
  if (!book) {
    return (
      <div className="w-full mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-black">Product not found</h2>
        <Link to="/shop" className="text-orange-600 hover:underline mt-4 inline-block">
          Browse all products
        </Link>
      </div>
    );
  }

  return (
    <main className="bg-white min-h-screen">
      <SEO
        title={book.title}
        description={book.description ? book.description.slice(0, 160) : `Buy ${book.title} by ${book.author} on Ekamithila.`}
      />
      <div className="w-full  mx-auto px-4 py-4">
        <nav className="flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap text-xs text-black mb-3" aria-label="Breadcrumb">
          <Link to="/" className="shrink-0 hover:text-orange-600">
            Home
          </Link>
          <FiChevronRight className="shrink-0" aria-hidden="true" />
          <Link
            to={`/shop?category=${encodeURIComponent(categoryName).replace(/%20/g, '+')}`}
            className="max-w-[40%] shrink-0 truncate hover:text-orange-600"
            title={categoryName}
          >
            {categoryName}
          </Link>
          <FiChevronRight className="shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-black" aria-current="page" title={book.title}>{book.title}</span>
        </nav>

        <section className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] lg:grid-cols-[250px_minmax(0,1fr)_360px] xl:grid-cols-[250px_minmax(0,1fr)_360px] gap-5 lg:gap-7 items-start">
          <figure className="m-0  mx-auto md:mx-0 w-full relative pt-1">
            {marketDiscount > 0 && (
              <span className="absolute -left-3 top-1 z-10 w-[100px] bg-red-600 py-1 text-center text-sm font-bold text-white shadow-sm before:absolute before:left-0 before:top-full before:border-r-[12px] before:border-t-[10px] before:border-r-transparent before:border-t-red-800">
                {marketDiscount}%
              </span>
            )}
            <ImageZoom src={getBookImage(book.image)} alt={book.title} />
            <StockOverlay stock={book.stock} />
          </figure>

          <ProductInfo
            book={book}
            isBook={isBook}
            avgRating={avgRating}
            reviewCount={reviews.length}
            categoryName={categoryName}
          />

          <BuyBox
            book={book}
            qty={qty}
            onQtyChange={setQty}
            isOutOfStock={isOutOfStock}
            inWishlist={inWishlist}
            onAddToCart={handleAddToCart}
            onBuyNow={handleBuyNow}
            onWishlist={handleWishlist}
          />

        </section>

        {isBook && <AttributesSlider book={book} />}

        {!isBook && (
          <section className="mt-6">
            <h2 className="text-xl font-bold text-black mb-3">Product description</h2>
            <ProductDescription text={book.description} />
          </section>
        )}

        <ProductDetailsBlock
          book={book}
          isBook={isBook}
          avgRating={avgRating}
          reviewCount={reviews.length}
          categoryName={categoryName}
        />

        <ReviewsSection
          book={book}
          reviews={reviews}
          avgRating={avgRating}
          ratingDistribution={ratingDistribution}
          isAuthenticated={isAuthenticated}
          showReviewForm={showReviewForm}
          setShowReviewForm={setShowReviewForm}
          reviewData={reviewData}
          setReviewData={setReviewData}
          reviewSort={reviewSort}
          setReviewSort={setReviewSort}
          sortedReviews={sortedReviews}
          displayReviews={displayReviews}
          onSubmitReview={handleSubmitReview}
          onHelpful={handleHelpful}
          helpfulReviewIds={helpfulReviewIds}
          helpfulPendingIds={helpfulPendingIds}
        />

        <RelatedProducts items={related} />
        <RelatedProducts
          title="New Releases"
          items={newReleases
            .filter((item) => !related.some((relatedItem) => relatedItem.id === item.id))
            .slice(0, 6)}
        />
      </div>
    </main>
  );
};

export default ProductDetail;
